import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';

import { GET_ALL_STATES } from '../constants/api-endpoints';
import { US_STATES } from '../constants/us-states';
import { State } from '../../models/common/family-profile';

/**
 * US states for every state dropdown, from `Master/GetAllStates`. Mapped to the
 * `{ name, abbreviation }` shape the dropdowns bind (`optionLabel="name"`,
 * `optionValue="abbreviation"`), so the stored value stays the postal code.
 *
 * The endpoint needs a signed-in user; only call it from authenticated screens.
 */
@Injectable({ providedIn: 'root' })
export class StateService {
  private readonly http = inject(HttpClient);

  /** Static master data, so the first request is shared for the session. */
  private states$?: Observable<State[]>;

  /** Active states, sorted by the API. Falls back to the bundled list on a
   *  failed or empty response, so a required State field never blocks a save. */
  getStates(): Observable<State[]> {
    this.states$ ??= this.http.post<any>(GET_ALL_STATES(), {}).pipe(
      map((response) => {
        const rows = response?.data ?? response;
        const states: State[] = (Array.isArray(rows) ? rows : [])
          .filter((row: any) => (row?.isActive ?? row?.IsActive) !== false)
          .map((row: any) => ({
            name: row?.stateName ?? row?.StateName,
            abbreviation: row?.stateCode ?? row?.StateCode
          }))
          .filter((state) => !!state.name && !!state.abbreviation);
        return states.length ? states : US_STATES;
      }),
      catchError(() => {
        // Don't pin the fallback for the session; the next caller retries.
        this.states$ = undefined;
        return of(US_STATES);
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );
    return this.states$;
  }
}
